/**
 * Labstep
 *
 * @module grid/BatchCreateResourceItems/coldefs/locationPath
 * @desc Column Definition for location path
 */

import type { ValueGetterParams } from 'ag-grid-community';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Flex } from 'labstep-web/core/Flex';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';
import { Icon } from 'semantic-ui-react';

export const getLocationPathColDef = (
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
): BatchCreateResourceItemsColDef => {
  return {
    colId: 'resource_location_path',
    headerName: 'Location',
    headerComponentParams: {
      icon: ICONS.resource_location.primary,
      info: 'i.e. Fridge A > Box 1. This will look for a sub-location called Box 1 within a location called Fridge A. If no locations with those name are found they will be created.',
    },
    editable: true,
    valueGetter: (params: ValueGetterParams) =>
      params.node?.data?.resource_location_path || '',
    valueSetter:
      GridBatchCreateResourceItemsService.getValueSetterForColId(
        'resource_location_path',
        data,
        setData,
      ),
    cellRenderer: (params: CellRendererParams<ResourceItemData>) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );

      const row = params.node.data;
      if (row.resource_location_path) {
        return (
          <Flex spacing="between">
            <span>{row.resource_location_path}</span>
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </Flex>
        );
      }

      return (
        <Flex spacing="between">
          <DataGridPlaceholder
            params={params}
            editable={true}
            placeholder={'Enter'}
          />
          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </Flex>
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: () => null,
  };
};
